import { useCallback, useState } from 'react';
import { toaster } from '@optiaxiom/react';
import { useQueryClient } from '@tanstack/react-query';
import { Node, useReactFlow } from '@xyflow/react';

import { SpecializedAgent } from '@frontend/types';
import { Step, StepType, Trigger } from '@frontend/types/workflow-agent';

import { createSpecializedAgent, getSpecializedAgent } from '../../../services/specialized-agent';
import { wouldExceedNestingLimit } from '../utils/forLoopNestingUtils';

type NodeData = Step | Trigger;

export type PendingDrop = {
  nodeData: Step | Trigger;
  position: { x: number; y: number };
  forLoopContainer?: Node;
};

type DragData = {
  type: 'sidebar-item';
  nodeData: NodeData;
};

type UseWorkflowDndProps = {
  reactFlowWrapper: React.RefObject<HTMLDivElement>;
  nodes: Node[];
  instanceId: string;
  onDropNode: (
    nodeData: Step | Trigger,
    position: { x: number; y: number },
    forLoopContainer?: Node
  ) => void;
  onPendingDrop?: (pendingDrop: PendingDrop) => void;
};

const findForLoopContainerAtPosition = (
  clientX: number,
  clientY: number,
  nodes: Node[]
): Node | null => {
  const elementAtPosition = document.elementFromPoint(clientX, clientY);
  if (!elementAtPosition) return null;

  const forLoopContainers: string[] = [];
  let currentElement: Element | null = elementAtPosition;

  while (currentElement) {
    const forLoopId = currentElement.getAttribute('data-forloop-id');
    if (forLoopId) {
      forLoopContainers.push(forLoopId);
    }
    currentElement = currentElement.parentElement;
  }

  if (forLoopContainers.length === 0) return null;

  const targetForLoopId = forLoopContainers[0];

  return nodes.find(node => node.id === targetForLoopId && node.type === 'for_loop') || null;
};

export function useWorkflowDnd({
  reactFlowWrapper,
  nodes,
  instanceId,
  onDropNode,
  onPendingDrop,
}: UseWorkflowDndProps) {
  const { screenToFlowPosition } = useReactFlow();
  const queryClient = useQueryClient();
  const [pendingDrop, setPendingDrop] = useState<PendingDrop | null>(null);
  const [isCopying, setIsCopying] = useState(false);

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();

      if (!reactFlowBounds) {
        return;
      }

      try {
        const rawData = event.dataTransfer.getData('application/json');

        if (!rawData) {
          return;
        }

        const dragData: DragData = JSON.parse(rawData);

        if (dragData.type === 'sidebar-item' && dragData.nodeData) {
          const position = screenToFlowPosition({
            x: event.clientX - reactFlowBounds.left,
            y: event.clientY - reactFlowBounds.top,
          });

          const forLoopContainer = findForLoopContainerAtPosition(
            event.clientX,
            event.clientY,
            nodes
          );

          if (
            dragData.nodeData.type === 'for_loop' &&
            wouldExceedNestingLimit(forLoopContainer, nodes)
          ) {
            toaster.create('Cannot nest ForLoop more than 4 levels deep', {
              intent: 'danger',
            });
            return;
          }

          if (
            dragData.nodeData.type === 'specialized' &&
            dragData.nodeData.is_system_agent === true
          ) {
            const pending: PendingDrop = {
              nodeData: dragData.nodeData,
              position,
              forLoopContainer: forLoopContainer || undefined,
            };
            setPendingDrop(pending);
            onPendingDrop?.(pending);
            return;
          }

          onDropNode(dragData.nodeData, position, forLoopContainer || undefined);
        }
      } catch {
        console.warn('Failed to drop node');
      }
    },
    [screenToFlowPosition, reactFlowWrapper, nodes, onDropNode, onPendingDrop]
  );

  const handleConfirmDrop = useCallback(async () => {
    if (!pendingDrop) return;

    const nodeData = pendingDrop.nodeData;

    // Check if this is a specialized agent that needs to be copied
    if (nodeData.type === StepType.SPECIALIZED && 'links' in nodeData && nodeData.links?.self) {
      setIsCopying(true);
      try {
        // Fetch the full agent details
        const fullAgent = await getSpecializedAgent(nodeData.links.self)();

        // Create a copy with a new name
        const copiedAgent: SpecializedAgent = {
          ...fullAgent,
          name: `${fullAgent.name} (Copy)`,
          agent_id: `${fullAgent.agent_id}_copy_${new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14)}`,
          locked_fields: [],
          is_system_agent: false,
        };

        // Create the copy in the backend
        const response = await createSpecializedAgent(copiedAgent, instanceId)();
        const createdAgent = response.data as SpecializedAgent;

        // Create node data for the copied agent
        const copiedNodeData: Step = {
          type: StepType.SPECIALIZED,
          agent_id: createdAgent.id,
          name: createdAgent.name || '',
          description: createdAgent.description,
          step_id: createdAgent.id,
          is_system_agent: false,
        };

        onDropNode(copiedNodeData, pendingDrop.position, pendingDrop.forLoopContainer);

        // Invalidate the agents list so the sidebar shows the new agent
        queryClient.invalidateQueries({ queryKey: ['opal:specialized-agents'] });

        toaster.create('Agent copied successfully', { intent: 'success' });
      } catch {
        toaster.create('Failed to copy agent', { intent: 'danger' });
      } finally {
        setIsCopying(false);
      }
    } else {
      onDropNode(nodeData, pendingDrop.position, pendingDrop.forLoopContainer);
    }

    setPendingDrop(null);
  }, [pendingDrop, onDropNode, instanceId, queryClient]);

  const handleCancelDrop = useCallback(() => {
    setPendingDrop(null);
  }, []);

  return {
    onDragOver,
    onDrop,
    pendingDrop,
    isCopying,
    onConfirmDrop: handleConfirmDrop,
    onCancelDrop: handleCancelDrop,
  };
}
